import { useMemo } from 'react';
import {
    getProjectCatalogProductFileThumbnailUrl,
    getProjectCatalogProductFileUrl,
} from '@/services/project-catalog';
import {
    getOptionPresetImageThumbnailUrl,
    getOptionPresetImageUrl,
} from '@/services/option-preset/option-preset-image/option-preset-image.api';
import { useOptionConfiguratorOptional } from './OptionConfiguratorContext';

export interface ConfiguratorImageUrls {
    getProjectCatalogProductFileUrl: (productImageId: number, options?: { download?: boolean }) => string;
    getProjectCatalogProductFileThumbnailUrl: (productImageId: number) => string;
    getOptionPresetImageUrl: (imageId: number) => string;
    getOptionPresetImageThumbnailUrl: (imageId: number) => string;
}

/**
 * Returns the configurator image-URL helpers pre-bound to the current `showroomPath`, so call
 * sites can request URLs by id alone and stay unaware of the showroom mechanism. In the
 * authenticated flow `showroomPath` is undefined and the helpers return the auth-gated URLs; in
 * the anonymous showroom they return the path-scoped public URLs.
 *
 * Reads the configurator context optionally: seller-side pages reuse configurator components (the
 * product more-info modal) outside any OptionConfiguratorProvider. There is no showroom there, so
 * `showroomPath` stays undefined and the helpers return the auth-gated URLs.
 */
export function useImageUrls(): ConfiguratorImageUrls {
    const showroomPath = useOptionConfiguratorOptional()?.showroomPath;
    return useMemo(
        () => ({
            getProjectCatalogProductFileUrl: (productImageId, options) =>
                getProjectCatalogProductFileUrl(productImageId, { ...options, showroomPath }),
            getProjectCatalogProductFileThumbnailUrl: (productImageId) =>
                getProjectCatalogProductFileThumbnailUrl(productImageId, { showroomPath }),
            getOptionPresetImageUrl: (imageId) => getOptionPresetImageUrl(imageId, { showroomPath }),
            getOptionPresetImageThumbnailUrl: (imageId) =>
                getOptionPresetImageThumbnailUrl(imageId, { showroomPath }),
        }),
        [showroomPath]
    );
}
